import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface CookieContextType {
  cookiesAccepted: boolean | null;
  acceptCookies: () => void;
  declineCookies: () => void;
  hasConsent: boolean;
}

const CookieContext = createContext<CookieContextType | undefined>(undefined);

export const useCookies = () => {
  const context = useContext(CookieContext);
  if (context === undefined) {
    throw new Error('useCookies must be used within a CookieProvider');
  }
  return context;
};

interface CookieProviderProps {
  children: ReactNode;
}

export const CookieProvider: React.FC<CookieProviderProps> = ({ children }) => {
  const [cookiesAccepted, setCookiesAccepted] = useState<boolean | null>(null);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (consent === 'accepted') {
      setCookiesAccepted(true);
    } else if (consent === 'declined') {
      setCookiesAccepted(false);
    }
  }, []);

  const acceptCookies = () => {
    setCookiesAccepted(true);
    localStorage.setItem('cookieConsent', 'accepted');
    
    // Enable analytics or other tracking here
    console.log('Cookies accepted - enabling analytics');
  };

  const declineCookies = () => {
    setCookiesAccepted(false);
    localStorage.setItem('cookieConsent', 'declined');
    
    // Disable analytics or other tracking here
    console.log('Cookies declined - disabling analytics');
  };

  const value = {
    cookiesAccepted,
    acceptCookies,
    declineCookies,
    hasConsent: cookiesAccepted !== null,
  };

  return <CookieContext.Provider value={value}>{children}</CookieContext.Provider>;
};